/**
 * What a system admin sees after reading a schedule off a link, before anything
 * is committed.
 *
 * A file import auto-advances: there is nothing to correct and nothing to
 * re-read, so making the coach click through would be friction for its own sake.
 * A link import does not, for a reason that is easy to lose: advancing runs the
 * confirm bridge, which creates the draft tournament and its games, and a
 * confirmed ingestion can no longer be re-extracted. Auto-advancing would close
 * the correction loop before the admin had seen a single extracted game.
 *
 * Two ways to correct, because that is how this actually goes. Re-prompt while
 * the whole extraction is wrong in a describable way, then hand-fix the last few
 * rows on the next step once it is close.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getScheduleIngestionReview,
  getScheduleIngestionStatus,
  reextractSchedule,
} from '../../../../../services/schedule-ingestions';
import { getSafeErrorMessage } from '../../../../../utils/errorHandler';
import type { ExtractedGame } from '../../../../../types/schedule-ingestion';
import { diffGames, isNoOpDiff, type GameDiff } from './gameDiff';

/** Matches the run hook; a replay is one model call, so this is generous. */
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

interface LinkImportReviewProps {
  ingestionId: string;
  /** The link that was read, shown so the admin can confirm they got the page they meant. */
  sourceUrl: string;
  onContinue: () => void;
  onStartOver: () => void;
  /** Focus target for the primary action, so the long async read does not strand keyboard users. */
  continueRef?: React.RefObject<HTMLButtonElement | null>;
}

/** Distinct game dates, in the order the schedule listed them. */
function distinctDates(games: ExtractedGame[]): string[] {
  const seen = new Set<string>();
  const dates: string[] = [];
  for (const game of games) {
    const date = game.date?.trim();
    if (date && !seen.has(date)) {
      seen.add(date);
      dates.push(date);
    }
  }
  return dates;
}

export function LinkImportReview({
  ingestionId,
  sourceUrl,
  onContinue,
  onStartOver,
  continueRef,
}: LinkImportReviewProps) {
  const [games, setGames] = useState<ExtractedGame[] | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  // The rendered page's <title>. On zero games this is the most diagnostic thing
  // available: it distinguishes "we read the schedule and could not parse it"
  // from "we rendered a consent page or a bot wall".
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [canReextract, setCanReextract] = useState(true);
  const [adjustments, setAdjustments] = useState({ months: 0, merged: 0 });
  const [error, setError] = useState('');

  const [feedback, setFeedback] = useState('');
  const [applied, setApplied] = useState<string[]>([]);
  const [rereading, setRereading] = useState(false);
  const [correctionError, setCorrectionError] = useState('');
  const [diff, setDiff] = useState<GameDiff | null>(null);

  const controllerRef = useRef<AbortController | null>(null);

  const loadReview = useCallback(
    async (signal: AbortSignal) => {
      const review = await getScheduleIngestionReview(ingestionId, signal);
      setProfileName(review.scrapeProfileName ?? null);
      setPageTitle(review.suggestedName ?? null);
      setApplied(review.appliedCorrections ?? []);
      // Mirrors the server's guards, so we never offer a correction box that the
      // endpoint would refuse.
      setCanReextract(review.canReextract ?? true);
      setAdjustments({
        months: review.monthCorrections ?? 0,
        merged: review.mergedDuplicates ?? 0,
      });
      return review.games;
    },
    [ingestionId]
  );

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;

    loadReview(controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setGames(loaded);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(
          getSafeErrorMessage(err, "We couldn't load what was read from that link.")
        );
      });

    return () => controller.abort();
  }, [loadReview]);

  /**
   * Queues the correction, waits for the replay, then shows what moved.
   *
   * The previous games are captured BEFORE the request so the diff compares the
   * two extractions rather than comparing the new one against itself.
   */
  const submitCorrection = async () => {
    const trimmed = feedback.trim();
    if (!trimmed || rereading) return;

    const signal = controllerRef.current?.signal;
    const previous = games ?? [];

    setRereading(true);
    setCorrectionError('');
    setDiff(null);

    try {
      const queued = await reextractSchedule(ingestionId, trimmed, signal);
      if (signal?.aborted) return;

      setApplied(queued.feedback);
      setFeedback('');

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      // Poll the ordinary status endpoint: a replay is an ordinary run to
      // everything downstream, which is the point of queuing it as one.
      for (;;) {
        if (signal?.aborted) return;
        if (Date.now() > deadline) {
          setCorrectionError('That re-read is taking too long. Try again.');
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (signal?.aborted) return;

        const status = await getScheduleIngestionStatus(ingestionId, signal);
        if (status.status === 'Failed') {
          setCorrectionError(status.error || "We couldn't re-read that page.");
          return;
        }
        if (status.status !== 'Completed') continue;

        const reloaded = await loadReview(signal!);
        if (signal?.aborted) return;

        setGames(reloaded);
        setDiff(diffGames(previous, reloaded));
        return;
      }
    } catch (err) {
      if (signal?.aborted) return;
      setCorrectionError(
        getSafeErrorMessage(err, "We couldn't apply that correction.")
      );
    } finally {
      setRereading(false);
    }
  };

  if (error) {
    return (
      <div className="wiz-error-box" data-testid="wizard-link-review-error">
        <p role="alert">{error}</p>
        <button type="button" className="wiz-btn" onClick={onStartOver}>
          Try a different link
        </button>
      </div>
    );
  }

  if (!games) {
    return (
      <div className="wiz-progress" data-testid="wizard-link-review-loading">
        <span className="wiz-spinner" aria-hidden="true" />
        <span>Loading what we read...</span>
      </div>
    );
  }

  const dates = distinctDates(games);
  const host = (() => {
    try {
      return new URL(sourceUrl).host;
    } catch {
      return sourceUrl;
    }
  })();

  return (
    <div className="wiz-link-review" data-testid="wizard-link-review">
      <div className="wiz-note">
        <span className="wiz-note__icon" aria-hidden="true">
          i
        </span>
        {games.length === 0 ? (
          <div data-testid="wizard-link-no-games">
            We rendered {host}
            {pageTitle ? (
              <>
                {' '}
                — the page called itself <b>&ldquo;{pageTitle}&rdquo;</b>
              </>
            ) : null}{' '}
            but read no games from it.
            <br />
            If that is not the schedule you expected, the link probably landed on
            a listing or consent page — try the page that shows the games
            themselves. If it <i>is</i> the right page, say where the games are
            below and we&rsquo;ll read it again without reloading it.
          </div>
        ) : (
        <div>
          Read <b>{games.length}</b> {games.length === 1 ? 'game' : 'games'}
          {dates.length > 0 && (
            <>
              {' '}
              across <b>{dates.length}</b>{' '}
              {dates.length === 1 ? 'day' : 'days'} ({dates.join(', ')})
            </>
          )}{' '}
          from {host}
          {profileName ? (
            <>
              {' '}
              using the <b>{profileName}</b> profile
            </>
          ) : (
            // Worth saying. A bad extraction otherwise gives no clue whether the
            // site's tuned profile applied at all or the generic prompt ran.
            <> using the default prompt</>
          )}
          .
          {dates.length === 1 && (
            // A one-day result and a genuine one-day tournament look identical
            // otherwise, and the difference decides whether the admin should go
            // paste the next day's URL.
            <>
              {' '}
              If this tournament runs longer than one day, the other days may be
              on their own pages — read each one.
            </>
          )}
        </div>
        )}
      </div>

      {games.length > 0 && (
      <div className="wiz-link-review__scroll">
        <table className="wiz-link-review__table">
          <caption className="wiz-link-review__caption">
            Games read from the link
          </caption>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Time</th>
              <th scope="col">Field</th>
              <th scope="col">Home</th>
              <th scope="col">Away</th>
            </tr>
          </thead>
          <tbody>
            {games.map((game) => (
              <tr key={game.sortOrder}>
                <td>{game.date || '—'}</td>
                <td>{game.time || '—'}</td>
                <td>{game.field || '—'}</td>
                <td>{game.homeTeam || '—'}</td>
                <td>{game.awayTeam || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {(adjustments.months > 0 || adjustments.merged > 0) && (
        <div className="wiz-note wiz-note--tight" data-testid="wizard-link-adjustments">
          <div>
            {adjustments.months > 0 && (
              <>
                We moved <b>{adjustments.months}</b>{' '}
                {adjustments.months === 1 ? 'game' : 'games'} whose date fell
                outside this event's range. Check those dates — if the range is
                wrong, the dates now are too.{' '}
              </>
            )}
            {adjustments.merged > 0 && (
              <>
                We merged <b>{adjustments.merged}</b> duplicate{' '}
                {adjustments.merged === 1 ? 'row' : 'rows'}. A real double-header
                at the same field looks the same to us, so check nothing is
                missing.
              </>
            )}
          </div>
        </div>
      )}

      {diff && (
        <div className="wiz-note wiz-note--tight" data-testid="wizard-link-diff">
          {isNoOpDiff(diff) ? (
            // Worth saying out loud. Silence after a correction reads as "it
            // worked", and the reviewer would go on believing a wrong schedule
            // had been fixed.
            <div>
              That correction changed nothing. Try describing the problem
              differently, or fix the rows by hand on the next step.
            </div>
          ) : (
            <div>
              Since the last read: <b>{diff.added.length}</b> added,{' '}
              <b>{diff.removed.length}</b> removed, <b>{diff.changed.length}</b>{' '}
              changed, {diff.unchanged} unchanged.
            </div>
          )}
        </div>
      )}

      {canReextract && (
      <div className="wiz-field">
        <label htmlFor="wizard-link-feedback">
          Something wrong? Tell us what to fix and we'll read the same page again
        </label>
        <textarea
          id="wizard-link-feedback"
          className="wiz-link-review__feedback"
          rows={2}
          value={feedback}
          placeholder="e.g. the dates are MM/DD, not DD/MM — and you missed the Field 4 games"
          onChange={(e) => setFeedback(e.target.value)}
          disabled={rereading}
          data-testid="wizard-link-feedback"
        />
        <button
          type="button"
          className="wiz-btn"
          onClick={submitCorrection}
          disabled={rereading || feedback.trim().length === 0}
          data-testid="wizard-link-reextract"
        >
          {rereading ? 'Re-reading...' : 'Re-read with this correction'}
        </button>

        {applied.length > 0 && (
          <ul className="wiz-link-review__applied" data-testid="wizard-link-applied">
            {applied.map((note, i) => (
              <li key={`${i}-${note}`}>{note}</li>
            ))}
          </ul>
        )}

        {correctionError && (
          <p className="wiz-error" role="alert" data-testid="wizard-link-correction-error">
            {correctionError}
          </p>
        )}
      </div>
      )}

      {!canReextract && applied.length > 0 && (
        // Points somewhere rather than dead-ending: a loop that has not converged
        // is not going to, and the rows can still be fixed by hand on the next step.
        <div className="wiz-note wiz-note--tight" data-testid="wizard-link-reextract-exhausted">
          <div>
            We've re-read this page as many times as we can. Fix whatever is still
            wrong on the next step, where every row is editable.
          </div>
        </div>
      )}

      <div className="wiz-foot">
        <button
          type="button"
          className="wiz-btn"
          onClick={onStartOver}
          data-testid="wizard-link-start-over"
        >
          Read a different link
        </button>
        <button
          type="button"
          ref={continueRef}
          className="wiz-btn wiz-btn--primary"
          onClick={onContinue}
          disabled={games.length === 0 || rereading}
          data-testid="wizard-link-continue"
        >
          Looks right, continue →
        </button>
      </div>
    </div>
  );
}
